import React, { memo, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useDispatch } from 'react-redux';
import get from 'lodash/get';

import Actions from '@app/action';
import Assets from '@app/assets';
import Helper from '@app/helper';
import I18n from '@module/i18n';
import TextField from '@component/form/element/textfield';
import Icon from '@component/icon';
import Spinner from '@component/spinner';
import { WHITE } from '@app/constant/colors';
import { VALIDATION_IDENTIFIERS } from '@app/constant/validation-identifiers';
import { TRANSFER_STATES } from '@app/transfer-bundle/constant/transfer-states';

function EmailVerificationResend({ accountEmail }) {
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [email, setEmail] = useState(accountEmail);
  const [validState, setValidState] = useState(null);
  const [mobileError, setMobileError] = useState(
    'transfer.email-verification-resend.error'
  );

  async function handleSubmit(event) {
    event.preventDefault();

    try {
      setIsSubmitting(true);
      setValidState(null);
      await dispatch(Actions.User.resendVerifyAccountEmail(email));
    } catch (error) {
      if (get(error, 'body.fields.email', []).includes('taken')) {
        setMobileError('transfer.email-verification-resend.error-email-exists');
      } else {
        setMobileError('transfer.email-verification-resend.error');
      }
      setValidState(VALIDATION_IDENTIFIERS.NOT_VALID);
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleBackButtonClick() {
    dispatch(Actions.Notification.clearTransfer());
    dispatch(Actions.Transfer.setState(TRANSFER_STATES.VERIFY_ACCOUNT));
  }

  const isSubmitButtonActive = Helper.validateEmail(email);
  const submitButtonClassName = classnames(
    'transfer__button',
    'email-verification__submit-button',
    {
      'transfer__button--inactive': !isSubmitButtonActive,
    }
  );

  return (
    <form
      className="transfer__window email-verification"
      onSubmit={handleSubmit}
    >
      {validState === VALIDATION_IDENTIFIERS.NOT_VALID && (
        <div className="email-verification__mobile-error">
          {I18n.t(mobileError)}
        </div>
      )}
      <div className="transfer__contents email-verification__contents">
        <img
          className="email-verification__lock--big"
          src={Assets.lookup('transfer_window/email-verification-lock.svg')}
          alt="Lock"
        />
        <h2 className="email-verification__title">
          {I18n.t('transfer.email-verification-resend.title')}
        </h2>
        <p className="email-verification__body">
          {I18n.t('transfer.email-verification-resend.body')}
        </p>
        <TextField
          name="email"
          type="email"
          placeholder={I18n.t('transfer.email-verification-resend.email')}
          className="email-verification__textfield"
          inputClassName="email-verification__textfield-input"
          onChange={(event) => setEmail(event.target.value)}
          value={email}
          valid={validState}
        />
      </div>
      <div className="transfer__footer email-verification__footer">
        <Icon
          type="TransferBack"
          className="transfer__back"
          onClick={handleBackButtonClick}
          label={I18n.t('transfer.email-verification-resend.back')}
          buttonType="button"
        />
        <button
          className={submitButtonClassName}
          onClick={handleSubmit}
          disabled={!isSubmitButtonActive}
          type="submit"
        >
          {isSubmitting ? (
            <Spinner
              rotate={true}
              percentage={25}
              hideText
              circumference={24}
              stroke={3}
              className="email-verification__spinner"
              foreground={WHITE}
            />
          ) : (
            I18n.t('transfer.email-verification-resend.submit')
          )}
        </button>
      </div>
    </form>
  );
}

EmailVerificationResend.propTypes = {
  accountEmail: PropTypes.string.isRequired,
};

export default memo(EmailVerificationResend);
